import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ArrowLeftToLine,
  ArrowRightToLine,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Settings,
  User,
  Users,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  REGION_KEYS_BY_NAME,
  REGION_LABELS_BY_ID,
  useGetSchedulerGroupsQuery,
  useGetSchedulerUserSettingsQuery,
  useSchedulerReferenceUsers,
} from '@/app/services/serviceScheduler';
import { openModal as openAdminModal } from '@/app/slices/adminSettingsModalSlice';
import { ChipSelector } from '@/components/common';
import {
  AdminSettingsModal,
  CreateEventModal,
  CreateTripModal,
  type DateBoxView,
  DateSelector,
  MainCalendar,
  MobileFooter,
  MobileFooterButton,
  Button as SchedulerButton,
  ServiceSchedulerDrawer,
  ServiceSchedulerHeader,
  ServiceSchedulerViewList,
  ViewSelector,
} from '@/components/common/service-scheduler';
import { useSitesPage } from '@/services/auth';
import { getLocalStorage, setLocalStorage } from '@/utils/storage';

const regionKeysByName = REGION_KEYS_BY_NAME;
const NONE_TECHS_TOKEN = '__none__';

export function ServiceScheduler() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRole = searchParams.get('role');
  const initialRoleFilter: 'all' | 'pse' | 'psv' =
    initialRole === 'pse' || initialRole === 'psv' || initialRole === 'all'
      ? initialRole
      : 'all';
  const initialRegions =
    searchParams
      .get('regions')
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) ?? [];
  const initialSites =
    searchParams
      .get('sites')
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) ?? [];
  const initialGroups =
    searchParams
      .get('groups')
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) ?? [];
  const initialTechsParam = searchParams.get('techs');
  const initialSidebar = searchParams.get('sidebar');
  const sidebarPrefs = getLocalStorage('serviceScheduler') ?? {};
  const initialSidebarSide: 'left' | 'right' =
    sidebarPrefs.sidebarSide === 'right'
      ? 'right'
      : initialSidebar === 'right'
        ? 'right'
        : 'left';
  const initialSelectedTechIdsRef = useRef<Set<string> | null>(
    initialTechsParam === NONE_TECHS_TOKEN
      ? new Set<string>()
      : (initialTechsParam
          ?.split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean)
          .reduce((set, id) => {
            set.add(id);
            return set;
          }, new Set<string>()) ?? null),
  );
  const [view, setView] = useState<DateBoxView>('month');
  const [activeMonth, setActiveMonth] = useState(() => new Date());
  const [mobilePanel, setMobilePanel] = useState<'calendar' | 'users'>(
    'calendar',
  );
  const [sidebarSide, setSidebarSide] = useState<'left' | 'right'>(
    initialSidebarSide,
  );
  const [isSidebarVisible, setIsSidebarVisible] = useState(
    sidebarPrefs.sidebarVisible ?? true,
  );
  const [roleFilter, setRoleFilter] = useState<'all' | 'pse' | 'psv'>(
    initialRoleFilter,
  );
  const [selectedRegions, setRegions] = useState<string[]>(initialRegions);
  const [selectedSiteCodes, setSelectedSiteCodes] =
    useState<string[]>(initialSites);
  const [selectedGroupIds, setSelectedGroupIds] =
    useState<string[]>(initialGroups);
  const [activeUsers, setActiveUsers] = useState<Record<string, boolean>>({});
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [sitePageNumber, setSitePageNumber] = useState(1);
  const { users: referenceUsersRaw } = useSchedulerReferenceUsers({
    activeOnly: true,
    startRow: 1,
    endRow: 1000,
  });
  const { data: groups = [] } = useGetSchedulerGroupsQuery();
  const { data: userSettings = [] } = useGetSchedulerUserSettingsQuery();
  const { data: sitesData, isFetching: isFetchingSites } = useSitesPage({
    pageNumber: sitePageNumber,
    pageSize: 100,
    searchTerm: siteSearchTerm,
  });
  const { data: allSitesData } = useSitesPage({
    pageNumber: 1,
    pageSize: 1000,
  });
  const [loadedSiteOptions, setLoadedSiteOptions] = useState<
    { name: string; code: string; siteId: string; region: number }[]
  >([]);
  const siteOptions = useMemo(
    () =>
      loadedSiteOptions.map((site) => ({
        name: site.name,
        code: site.code,
      })),
    [loadedSiteOptions],
  );
  useEffect(() => {
    if (!sitesData?.items) return;
    setLoadedSiteOptions((prev) => {
      const incoming = sitesData.items.map((site) => ({
        name: site.name,
        code: site.code,
        siteId: site.siteId,
        region: Number(site.region ?? 0),
      }));
      if (sitePageNumber === 1) {
        const byCode = new Map(incoming.map((site) => [site.code, site]));
        for (const site of prev) {
          if (selectedSiteCodes.includes(site.code)) {
            byCode.set(site.code, site);
          }
        }
        return Array.from(byCode.values());
      }
      const byCode = new Map(prev.map((site) => [site.code, site]));
      for (const site of incoming) byCode.set(site.code, site);
      return Array.from(byCode.values());
    });
  }, [sitesData?.items, sitePageNumber, selectedSiteCodes]);
  const regions = useMemo(() => {
    const regionIds = new Set(
      (allSitesData?.items ?? [])
        .map((site) => Number(site.region ?? 0))
        .filter((id) => id > 0),
    );
    return Array.from(regionIds)
      .sort((a, b) => a - b)
      .map((id) => ({
        name: REGION_LABELS_BY_ID[id] ?? `Region ${id}`,
        id: REGION_LABELS_BY_ID[id] ?? String(id),
      }));
  }, [allSitesData?.items]);

  const siteRegionBySiteId = useMemo(
    () =>
      new Map(
        (allSitesData?.items ?? []).map((site) => [
          site.siteId.toLowerCase(),
          Number(site.region ?? 0),
        ]),
      ),
    [allSitesData?.items],
  );
  const siteRegionBySiteName = useMemo(
    () =>
      new Map(
        (allSitesData?.items ?? []).map((site) => [
          (site.name ?? '').trim().toLowerCase(),
          Number(site.region ?? 0),
        ]),
      ),
    [allSitesData?.items],
  );
  const siteCodeBySiteId = useMemo(
    () =>
      new Map(
        (allSitesData?.items ?? []).map((site) => [
          site.siteId.toLowerCase(),
          (site.code ?? '').toLowerCase(),
        ]),
      ),
    [allSitesData?.items],
  );
  const siteCodeBySiteName = useMemo(
    () =>
      new Map(
        (allSitesData?.items ?? []).map((site) => [
          (site.name ?? '').trim().toLowerCase(),
          (site.code ?? '').toLowerCase(),
        ]),
      ),
    [allSitesData?.items],
  );
  const selectedRegionIds = useMemo(
    () =>
      new Set(
        selectedRegions
          .flatMap(
            (name) =>
              regionKeysByName[name.toLowerCase()] ?? [name.toLowerCase()],
          )
          .map((value) => value.toLowerCase()),
      ),
    [selectedRegions],
  );
  const selectedSiteCodeSet = useMemo(
    () => new Set(selectedSiteCodes.map((code) => code.toLowerCase())),
    [selectedSiteCodes],
  );
  const referenceUsers = useMemo(
    () =>
      Array.from(
        new Map(
          referenceUsersRaw
            .filter((user) => user.isPse || user.isPsv || user.isTechnician)
            .map((user) => [user.equipmentId.toLowerCase(), user]),
        ).values(),
      ),
    [referenceUsersRaw],
  );
  const groupOptions = useMemo(
    () =>
      groups.map((group) => ({
        id: group.groupId,
        name: group.name,
      })),
    [groups],
  );
  const selectedGroupUserIdSet = useMemo(() => {
    if (!selectedGroupIds.length) return null;
    const selectedSet = new Set(selectedGroupIds.map((id) => id.toLowerCase()));
    const userIdSet = new Set<string>();
    for (const group of groups) {
      if (!selectedSet.has(group.groupId.toLowerCase())) continue;
      for (const userId of group.userIds) {
        userIdSet.add(userId.toLowerCase());
      }
    }
    return userIdSet;
  }, [groups, selectedGroupIds]);
  const userSettingsById = useMemo(() => {
    const byId = new Map(
      userSettings.map((setting) => [setting.userId.toLowerCase(), setting]),
    );
    return byId;
  }, [userSettings]);

  const filteredReferenceUsers = useMemo(
    () =>
      referenceUsers.filter((user) => {
        if (roleFilter === 'pse' && !user.isPse) return false;
        if (roleFilter === 'psv' && !user.isPsv) return false;
        if (
          selectedGroupUserIdSet &&
          !selectedGroupUserIdSet.has(user.equipmentId.toLowerCase()) &&
          !(
            user.systemUserId &&
            selectedGroupUserIdSet.has(user.systemUserId.toLowerCase())
          )
        ) {
          return false;
        }

        const siteId = user.siteId?.toLowerCase();
        const siteName = (user.siteName ?? '').trim().toLowerCase();

        if (selectedSiteCodeSet.size) {
          const siteCode =
            (siteId ? siteCodeBySiteId.get(siteId) : undefined) ??
            (siteName ? siteCodeBySiteName.get(siteName) : undefined);
          if (!siteCode || !selectedSiteCodeSet.has(siteCode)) {
            return false;
          }
        }

        if (!selectedRegionIds.size) return true;

        const regionId =
          (siteId ? siteRegionBySiteId.get(siteId) : undefined) ??
          (siteName ? siteRegionBySiteName.get(siteName) : undefined);
        if (regionId == null) return false;
        return selectedRegionIds.has(String(regionId).toLowerCase());
      }),
    [
      referenceUsers,
      roleFilter,
      selectedGroupUserIdSet,
      selectedSiteCodeSet,
      selectedRegionIds,
      siteCodeBySiteId,
      siteCodeBySiteName,
      siteRegionBySiteId,
      siteRegionBySiteName,
    ],
  );

  const users = filteredReferenceUsers.map((user) => {
    const setting =
      userSettingsById.get(user.equipmentId.toLowerCase()) ??
      (user.systemUserId
        ? userSettingsById.get(user.systemUserId.toLowerCase())
        : undefined);
    return {
      id: user.equipmentId,
      name: user.name,
      color: 'sky' as const,
      colorHex: setting?.colorHex ?? '#0096ff',
    };
  });

  const activeUsersInitialized = useRef(false);
  useEffect(() => {
    if (users.length > 0 && !activeUsersInitialized.current) {
      activeUsersInitialized.current = true;
      const initialSelectedTechIds = initialSelectedTechIdsRef.current;
      if (initialSelectedTechIds) {
        setActiveUsers(
          Object.fromEntries(
            users
              .filter((u) => initialSelectedTechIds.has(u.id.toLowerCase()))
              .map((u) => [u.id, true]),
          ),
        );
      } else {
        setActiveUsers(Object.fromEntries(users.map((u) => [u.id, true])));
      }
    }
  }, [users]);

  useEffect(() => {
    if (!activeUsersInitialized.current || users.length === 0) return;
    setActiveUsers((previous) => {
      if (!Object.values(previous).some(Boolean)) {
        return previous;
      }

      let changed = false;
      const next = { ...previous };
      for (const user of users) {
        if (next[user.id] == null) {
          next[user.id] = true;
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [users]);

  useEffect(() => {
    setLocalStorage('serviceScheduler', sidebarSide, 'sidebarSide');
  }, [sidebarSide]);

  useEffect(() => {
    setLocalStorage('serviceScheduler', isSidebarVisible, 'sidebarVisible');
  }, [isSidebarVisible]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (roleFilter === 'all') {
      next.delete('role');
    } else {
      next.set('role', roleFilter);
    }
    if (selectedRegions.length) {
      next.set('regions', selectedRegions.join(','));
    } else {
      next.delete('regions');
    }
    if (selectedSiteCodes.length) {
      next.set('sites', selectedSiteCodes.join(','));
    } else {
      next.delete('sites');
    }
    if (selectedGroupIds.length) {
      next.set('groups', selectedGroupIds.join(','));
    } else {
      next.delete('groups');
    }
    if (sidebarSide === 'right') {
      next.set('sidebar', 'right');
    } else {
      next.delete('sidebar');
    }
    const selectedTechIds = users
      .filter((u) => !!activeUsers[u.id])
      .map((u) => u.id);
    if (!users.length || selectedTechIds.length === users.length) {
      next.delete('techs');
    } else if (selectedTechIds.length === 0) {
      next.set('techs', NONE_TECHS_TOKEN);
    } else {
      next.set('techs', selectedTechIds.join(','));
    }

    const current = searchParams.toString();
    const updated = next.toString();
    if (current !== updated) {
      setSearchParams(next, { replace: true });
    }
  }, [
    roleFilter,
    selectedRegions,
    selectedSiteCodes,
    selectedGroupIds,
    sidebarSide,
    activeUsers,
    users,
    searchParams,
    setSearchParams,
  ]);

  return (
    <div className="@container flex h-full flex-col gap-2">
      <ServiceSchedulerHeader className="rounded-lg border border-gray-200 bg-white px-3 py-3 shadow-sm">
        <div className="order-3 flex w-full flex-wrap items-center gap-2 @lg:order-1">
          <ChipSelector
            className="min-w-[180px] flex-1 text-sm"
            value={selectedRegions}
            onChange={setRegions}
            options={regions}
            optionId="id"
            optionLabel="name"
            placeholder="Regions"
            showClearAll
          />
          <ChipSelector
            className="min-w-[220px] flex-1 text-sm"
            value={selectedSiteCodes}
            onChange={setSelectedSiteCodes}
            options={siteOptions}
            optionId="code"
            optionLabel="name"
            loading={isFetchingSites}
            filter
            onFilter={(event) => {
              const nextSearch = (event.filter ?? '').trim();
              setSiteSearchTerm(nextSearch);
              setSitePageNumber(1);
            }}
            virtualScrollerOptions={{
              itemSize: 32,
              lazy: true,
              onLazyLoad: (event) => {
                if (!sitesData?.hasNext || isFetchingSites) return;
                if ((event.last ?? 0) >= siteOptions.length - 1) {
                  setSitePageNumber((prev) => prev + 1);
                }
              },
            }}
            scrollHeight="320px"
            maxSelectedLabels={2}
            placeholder="Sites"
            showClearAll
          />
          <ChipSelector
            className="min-w-[220px] flex-1 text-sm"
            value={selectedGroupIds}
            onChange={setSelectedGroupIds}
            options={groupOptions}
            optionId="id"
            optionLabel="name"
            maxSelectedLabels={2}
            placeholder="Groups"
            showClearAll
            emptyActionLabel="Create one"
            onClickEmptyAction={() =>
              dispatch(openAdminModal({ tab: 'groups' }))
            }
          />
          <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white p-0.5">
            <SchedulerButton
              type="button"
              label="All"
              size="small"
              severity={roleFilter === 'all' ? undefined : 'secondary'}
              className="h-7 px-2 text-xs"
              onClick={() => setRoleFilter('all')}
            />
            <SchedulerButton
              type="button"
              label="PSE"
              size="small"
              severity={roleFilter === 'pse' ? undefined : 'secondary'}
              className="h-7 px-2 text-xs"
              onClick={() => setRoleFilter('pse')}
            />
            <SchedulerButton
              type="button"
              label="PSV"
              size="small"
              severity={roleFilter === 'psv' ? undefined : 'secondary'}
              className="h-7 px-2 text-xs"
              onClick={() => setRoleFilter('psv')}
            />
          </div>
        </div>

        <DateSelector
          className="order-1 @lg:order-2 @lg:max-w-72"
          date={activeMonth}
          setDate={setActiveMonth}
          view={view}
        />

        <ViewSelector
          className="order-2 w-full @lg:order-3 @lg:max-w-80"
          setView={setView}
        />
      </ServiceSchedulerHeader>

      <div className="@container relative flex grow overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isSidebarVisible && sidebarSide === 'left' && (
          <ServiceSchedulerDrawer side="left">
            <ServiceSchedulerViewList
              users={users}
              activeUsers={activeUsers}
              onActiveUsersChange={setActiveUsers}
            />

            <hr className="w-full border-t-0 border-gray-200" />

            <SchedulerButton
              type="button"
              label="Admin Settings"
              icon="pi pi-cog"
              className="w-full rounded-md shadow-sm"
              onClick={() => dispatch(openAdminModal())}
            />
            <div className="mt-auto flex justify-start pt-2">
              <SchedulerButton
                type="button"
                size="small"
                severity="secondary"
                className="h-7 min-w-0 px-2"
                icon={<ArrowRightToLine className="size-3.5" />}
                onClick={() =>
                  setSidebarSide((side) => (side === 'left' ? 'right' : 'left'))
                }
              />
              <SchedulerButton
                type="button"
                size="small"
                severity="secondary"
                className="h-7 min-w-0 px-2"
                icon={<PanelLeftClose className="size-3.5" />}
                onClick={() => setIsSidebarVisible(false)}
              />
            </div>
          </ServiceSchedulerDrawer>
        )}

        <div
          className={`@container ${mobilePanel === 'users' ? 'hidden sm:flex' : 'flex'} h-full flex-1 flex-col gap-2 bg-white p-2`}
        >
          <div className="grow">
            <MainCalendar
              activeDate={activeMonth}
              setActiveDate={setActiveMonth}
              view={view}
              setView={setView}
              selectedRegions={selectedRegions}
              selectedSiteCodes={selectedSiteCodes}
              activeUsers={activeUsers}
              userSettings={userSettings}
              referenceUsers={filteredReferenceUsers}
            />
          </div>

          <MobileFooter>
            <MobileFooterButton
              isActive={mobilePanel === 'calendar'}
              onClick={() => setMobilePanel('calendar')}
            >
              <User />
            </MobileFooterButton>
            <MobileFooterButton
              isActive={mobilePanel === 'users'}
              onClick={() => setMobilePanel('users')}
            >
              <Users />
            </MobileFooterButton>
            <MobileFooterButton onClick={() => dispatch(openAdminModal())}>
              <Settings />
            </MobileFooterButton>
          </MobileFooter>
        </div>

        {isSidebarVisible && sidebarSide === 'right' && (
          <ServiceSchedulerDrawer side="right">
            <ServiceSchedulerViewList
              users={users}
              activeUsers={activeUsers}
              onActiveUsersChange={setActiveUsers}
            />

            <hr className="w-full border-t-0 border-gray-200" />

            <SchedulerButton
              type="button"
              label="Admin Settings"
              icon="pi pi-cog"
              className="w-full rounded-md shadow-sm"
              onClick={() => dispatch(openAdminModal())}
            />
            <div className="mt-auto flex justify-end pt-2">
              <SchedulerButton
                type="button"
                size="small"
                severity="secondary"
                className="h-7 min-w-0 px-2"
                icon={<PanelRightClose className="size-3.5" />}
                onClick={() => setIsSidebarVisible(false)}
              />
              <SchedulerButton
                type="button"
                size="small"
                severity="secondary"
                className="h-7 min-w-0 px-2"
                icon={<ArrowLeftToLine className="size-3.5" />}
                onClick={() =>
                  setSidebarSide((side) => (side === 'left' ? 'right' : 'left'))
                }
              />
            </div>
          </ServiceSchedulerDrawer>
        )}

        {!isSidebarVisible && (
          <div
            className={`pointer-events-none absolute bottom-2 z-10 flex ${
              sidebarSide === 'left'
                ? 'left-2 justify-start'
                : 'right-2 justify-end'
            }`}
          >
            <SchedulerButton
              type="button"
              size="small"
              severity="secondary"
              className="pointer-events-auto h-7 min-w-0 px-2"
              icon={
                sidebarSide === 'left' ? (
                  <PanelLeftOpen className="size-3.5" />
                ) : (
                  <PanelRightOpen className="size-3.5" />
                )
              }
              onClick={() => setIsSidebarVisible(true)}
            />
          </div>
        )}

        <div
          className={`${mobilePanel === 'users' ? 'flex sm:hidden' : 'hidden'} h-full w-full flex-col gap-3 bg-white p-3`}
        >
          <ServiceSchedulerViewList
            users={users}
            activeUsers={activeUsers}
            onActiveUsersChange={setActiveUsers}
          />

          <SchedulerButton
            type="button"
            label="Admin Settings"
            icon="pi pi-cog"
            className="w-full rounded-md shadow-sm"
            onClick={() => dispatch(openAdminModal())}
          />

          <MobileFooter>
            <MobileFooterButton
              isActive={mobilePanel === 'calendar'}
              onClick={() => setMobilePanel('calendar')}
            >
              <User />
            </MobileFooterButton>
            <MobileFooterButton
              isActive={mobilePanel === 'users'}
              onClick={() => setMobilePanel('users')}
            >
              <Users />
            </MobileFooterButton>
            <MobileFooterButton onClick={() => dispatch(openAdminModal())}>
              <Settings />
            </MobileFooterButton>
          </MobileFooter>
        </div>
      </div>

      <AdminSettingsModal />
      <CreateTripModal />
      <CreateEventModal />
    </div>
  );
}
